/**
 * @fileoverview Date formatting utilities with Thai/English locale support
 * @module utils/date
 */

/**
 * Thai month abbreviations
 */
const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

/**
 * Thai month full names
 */
const THAI_MONTHS_FULL = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

/**
 * English month abbreviations
 */
const EN_MONTHS_SHORT = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May.', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];

/**
 * English month full names
 */
const EN_MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Format date with various format options
 * @param {Date|string|number} date - Date to format
 * @param {string} format - Format string (dd/mm/yyyy, dd mmm yyyy, etc.)
 * @param {string} locale - Locale (en/th)
 * @param {boolean} type_excel - If true, parse Excel serial date
 * @returns {string} Formatted date string
 *
 * @example
 * format_date(new Date(), 'dd/mm/yyyy') // "04/03/2026"
 * format_date(new Date(), 'dd mmmm yyyy', 'th') // "04 มีนาคม 2026"
 * format_date(new Date(), 'dd mmm yyyy hh:mm', 'en') // "04 Mar. 2026 10:30"
 */
export const format_date = (date, format, locale, type_excel) => {
  const array_month = locale?.toLowerCase() === 'en' ? EN_MONTHS_SHORT : THAI_MONTHS_SHORT;
  const array_full_month = locale?.toLowerCase() === 'en' ? EN_MONTHS_FULL : THAI_MONTHS_FULL;

  let processedDate = date;

  // Handle Excel serial date format
  if (type_excel) {
    if (Number(date)) {
      processedDate = new Date(date * 24 * 60 * 60 * 1000 + Date.parse('1899-12-30'));
    } else if (typeof date === 'string') {
      const separator = date.includes('/') ? '/' : date.includes('.') ? '.' : null;
      if (separator && date.split(separator)[2]?.length === 4) {
        const [day, month, year] = date.split(separator);
        const adjustedYear = Number(year) < 2400 ? Number(year) : Number(year) - 543;
        processedDate = `${adjustedYear}-${('0' + month).slice(-2)}-${('0' + day).slice(-2)}`;
      }
    }
  }

  const new_date = new Date(processedDate);
  if (isNaN(new_date.getTime())) return '-';

  const day = new_date.getDate();
  const month = new_date.getMonth();
  const rawYear = new_date.getFullYear();

  // Always Gregorian (C.E.); legacy data stored in B.E. (> 2400) is converted back
  const year = rawYear > 2400 ? rawYear - 543 : rawYear;

  const hour = new_date.getHours();
  const minute = new_date.getMinutes();
  const second = new_date.getSeconds();

  // Default format
  if (!format) {
    return `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${String(year).padStart(4, '0')}`;
  }

  // Return timestamp
  if (format === 'date') {
    return new_date.getTime();
  }

  // Apply format replacements
  let formatted = format
    .toLowerCase()
    // Time formats
    .replace(/hh:mm:ss/g, `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`)
    .replace(/hh:mm/g, `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
    .replace(/hh/g, String(hour).padStart(2, '0'))
    .replace(/h:m:s/g, `${hour}:${minute}:${second}`)
    .replace(/h:m/g, `${hour}:${minute}`)
    // Day formats
    .replace(/dd/g, String(day).padStart(2, '0'))
    .replace(/\bd\b/g, day)
    // Month formats
    .replace(/mmmm/g, array_full_month[month])
    .replace(/mmm/g, array_month[month])
    .replace(/\bmm\b/g, String(month + 1).padStart(2, '0'))
    .replace(/\bm\b/g, (month + 1).toString())
    // Year formats
    .replace(/yyyy/g, String(year).padStart(4, '0'))
    .replace(/yyy/g, year.toString().slice(-3))
    .replace(/\byy\b/g, year.toString().slice(-2))
    .replace(/\by\b/g, year);

  return formatted || '';
};

/**
 * Format date to ISO string (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string} ISO date string
 */
export const toISODateString = (date) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Format date as dd/mm/yyyy (C.E.)
 * @param {Date|string} date - Date to format
 * @returns {string} Thai formatted date
 */
export const formatThaiDate = (date) => {
  return format_date(date, 'dd/mm/yyyy', 'th');
};

/**
 * Format date to English format
 * @param {Date|string} date - Date to format
 * @returns {string} English formatted date
 */
export const formatEnglishDate = (date) => {
  return format_date(date, 'dd/mm/yyyy', 'en');
};

const dateUtils = {
  format_date,
  toISODateString,
  formatThaiDate,
  formatEnglishDate,
  THAI_MONTHS_SHORT,
  THAI_MONTHS_FULL,
  EN_MONTHS_SHORT,
  EN_MONTHS_FULL,
};

export default dateUtils;
